// liveRegion.tsx — singleton off-screen live-region pair at the body root.
//
// Implements:
//   - Single `<div role="status" aria-live="polite" aria-atomic="false">`
//   - Single `<div role="alert"  aria-live="assertive" aria-atomic="false">`
// Mounted at the body root so every state-narration site updates the same
// off-screen slot and SR users hear one consistent announcement track.
//
// Per CP-4.2 (the alert→status flip pattern): consumers compute role/live
// via `useStateNarration({key, severity, text})` and pass the role to
// `useLiveRegion().announce(role, text)`. The flip itself is the hook's job;
// the live-region only renders the message into the matching slot.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { tokens } from '@/design/tokens';

export interface LiveRegionApi {
  announce(role: 'status' | 'alert', text: string): void;
  /** Imperatively clear both slots (e.g. on route change). */
  clear(): void;
}

const noop: LiveRegionApi = { announce: () => {}, clear: () => {} };
const LiveRegionContext = createContext<LiveRegionApi>(noop);

export function useLiveRegion(): LiveRegionApi {
  return useContext(LiveRegionContext);
}

const HIDDEN_STYLE = {
  position: 'absolute' as const,
  width: tokens.visuallyHidden.size,
  height: tokens.visuallyHidden.size,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  overflow: 'hidden',
  whiteSpace: 'nowrap' as const,
  border: 0,
  padding: 0,
  margin: -tokens.visuallyHidden.size,
};

export function LiveRegionProvider({ children }: { children: ReactNode }) {
  const [polite, setPolite] = useState<string>('');
  const [assertive, setAssertive] = useState<string>('');
  const tickRef = useRef<{ polite: number; assertive: number }>({ polite: 0, assertive: 0 });

  const announce = useCallback((role: 'status' | 'alert', text: string) => {
    if (role === 'alert') {
      tickRef.current.assertive += 1;
      // Append a zero-width space when the message is the same as the prior
      // one so SR re-announces. (Browsers dedupe identical aria-live text.)
      setAssertive((prev) => (prev === text ? `${text}\u200B` : text));
    } else {
      tickRef.current.polite += 1;
      setPolite((prev) => (prev === text ? `${text}\u200B` : text));
    }
  }, []);

  const clear = useCallback(() => {
    setPolite('');
    setAssertive('');
  }, []);

  useEffect(() => () => clear(), [clear]);

  const api = useMemo<LiveRegionApi>(() => ({ announce, clear }), [announce, clear]);

  return (
    <LiveRegionContext.Provider value={api}>
      {children}
      <div
        id="gv-live-region-polite"
        role="status"
        aria-live="polite"
        aria-atomic="false"
        style={HIDDEN_STYLE}
      >
        {polite}
      </div>
      <div
        id="gv-live-region-assertive"
        role="alert"
        aria-live="assertive"
        aria-atomic="false"
        style={HIDDEN_STYLE}
      >
        {assertive}
      </div>
    </LiveRegionContext.Provider>
  );
}
